<template>
  <v-container v-if="(isStaging || !isDev) && !currentUser" class="d-flex flex-column ga-4 justify-center align-center" height="100vh">
      <v-card variant="tonal" color="primary">
        <v-card-title class="d-flex flex-column ga-4 justify-center align-center">
            <v-img width="33vh" aspect-ratio="16/9" :src="defs?.links.logo_full"/> 
            Management Panel
        </v-card-title>
        <v-card-text v-if="isStaging" class="text-center">
            <v-divider class="my-4"/>
            <v-text-field
              v-model="password"
              label="Password"
              type="password"
              autocomplete="current-password"
            />
            <v-btn @click="signInWithStaging" :disabled="signingIn" color="blue">
              Sign in with Password
            </v-btn>
        </v-card-text>
        <v-card-text :style="{
          opacity: isStaging && '0.5' || undefined, 
          pointerEvents: isStaging && 'none' || undefined
        }" class="text-center">
            <v-divider class="my-4"/>
            <v-btn  v-if="!isCheckingAuth" @click="signInWithGoogle" :disabled="signingIn" color="blue">
              <v-progress-circular v-if="signingIn" indeterminate></v-progress-circular>
              <span v-else>Sign in with Google</span>
            </v-btn>
            <v-progress-circular v-else indeterminate></v-progress-circular>
        </v-card-text>
      </v-card>

      <v-card v-if="error" variant="tonal" color="error" max-width="800">
        <v-card-title>Error</v-card-title>
        <v-card-text class="text-center">
            <p  class="error">{{ error }}</p>
        </v-card-text>
      </v-card>
    </v-container>
    <slot v-else v-bind="{user: currentUser}"></slot>
  </template>
  
  <script setup lang="ts">



import { ref, onMounted, provide, onUnmounted } from 'vue';
import axios from "axios";
import { signInWithPopup, User } from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { useIntervalFn } from "@vueuse/core";
import { repo } from 'remult';
import { Defaults } from '@shared/schema';

const isDev = import.meta.env.DEV
const isStaging = !!import.meta.env.VITE_STAGING_MODE
const password = ref('')
const devUser = {
  displayName: 'DEV',
  email: 'DEVMAIL',
  uid: 'devuid'
}

const defs = ref()
onMounted(async ()=>{
  defs.value = await repo(Defaults).findFirst({platform: import.meta.env.VITE_PLATFORM})
})
provide('defs',defs)

const error = ref<string | null>(null);
const isCheckingAuth = ref<boolean>(true);
const currentUser = ref<Partial<User> | null>(isDev && !isStaging ? devUser : null);

const getAuthHeaders = async (user: User)=>{
  if(isStaging){
    return {
      'x-password': password.value
    }
  }
  const token = await user.getIdToken(true)
  return {
    'authorization': `Bearer ${token}`,
  }
}

async function refreshUser(){
  if(currentUser.value){
    await axios('/api/user/auth',{
      headers: await getAuthHeaders(currentUser.value as User)
    })
  }
}

const checkTimer = useIntervalFn(async ()=>{
  if(currentUser.value){
    const exp = await axios('/api/user/valid')
    const minutesTTL = exp.data.ttl / 60
    if(minutesTTL < 2){
      console.log("refreshing user session")
      await refreshUser()
    }
  }
},1000 * 60 * 30)
// }, 1000 * 15)

onUnmounted(()=>{
  checkTimer.pause()
})

async function onNewUser(_new: User | null){
  let authHeaders = {}

  if(!_new) checkTimer.pause()
  else checkTimer.resume()

  if(!_new) return

  authHeaders = await getAuthHeaders(_new)
  // remult.apiClient.httpClient = (
  //   input:any,
  //   init:any
  // ) => {
  //   return fetch(input, {
  //     ...init,
  //     headers: {
  //       ...init.headers,
  //       ...authHeaders
  //     },
  //   })
  // }
}

const verifyWithBackend = async (user: User) => {
  try {
    if(!user) throw new Error("user data error")
    const authHeaders = await getAuthHeaders(user)
    const response = await axios.get('/api/user/auth',{headers: {
      ...authHeaders
    }})
    if (response.status !== 200) {
      throw new Error('User not whitelisted');
    }
    return true;
  } catch (err) {
    await auth.signOut();
    error.value = 'Access denied. Contact administrator.';
    return false;
  }
};

const signingIn = ref(false)

const signInWithGoogle = async () => {
  try {
    signingIn.value = true
    const result = await signInWithPopup(auth, googleProvider);
    if (await verifyWithBackend(result.user)) {
      await onNewUser(result.user)
      currentUser.value = result.user
    }
  } catch (err:any) {
    if(err.message.startsWith('Firebase: Error'))
      error.value = 'Looks like you the login window was closed before you could log in. Try again'
    else
      error.value = err.message;
  }
  finally{
    signingIn.value = false
  }
};

onMounted(async () => {
  try {
    await auth.authStateReady();
    if (auth.currentUser) {
      if (await verifyWithBackend(auth.currentUser)) {
        await onNewUser(auth.currentUser)
        currentUser.value = auth.currentUser
      }
    }
  } catch (err) {
    console.error('Auth check error:', err);
    error.value = 'Authentication check failed';
  } finally {
    isCheckingAuth.value = false;
  }
});

function doLogOut(){
  currentUser.value = null
  auth.signOut()
  error.value = null
  onNewUser(null)
  checkTimer.pause()
}

provide('user',currentUser)
provide('logout',doLogOut)


async function signInWithStaging(){
  try {
    signingIn.value = true
    const headers = await getAuthHeaders({} as User)
    const response = await axios.get('/api/user/auth',{
      headers
    })
    console.log(response)
    if (response.status !== 200) {
      throw new Error('User not whitelisted');
    }
    currentUser.value = devUser
  } catch (err:any) {
    if(err.message.startsWith('Firebase: Error'))
      error.value = 'Looks like you the login window was closed before you could log in. Try again'
    else
      error.value = err.message;
  }
  finally{
    signingIn.value = false
  }
}
  </script>